import React, { useState, useEffect } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useVsiAppContext } from "../stores/VsiApp";
import styles from "../utils/themeStyles";
import { genericColors } from "../utils/chartUtils";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

require("highcharts/indicators/indicators")(Highcharts);
require("highcharts/indicators/pivot-points")(Highcharts);
require("highcharts/indicators/macd")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/map")(Highcharts);

function RadarChart({ categories, series, data, colors, title = "", height, valuePrefix}) {
	const { theme, isTabletScreen } = useVsiAppContext();
	const basicTextStyle = {
		color: styles[theme].textColor,
		fontFamily: styles[theme].fontFamily,
	};
	
	const updateSeries = (series, data) => {
		if (series) return series;
		else if (data)
			return [
				{
					data,
				},
			];
		return [];
	};
	
	const [chartOptions, setChartOptions] = useState(
		{
			mapNavigation: {
				enabled: false,
			},
			colors: colors || genericColors,
			chart: {
				backgroundColor: 'transparent' || styles[theme].cardBgColor,
				color: styles[theme].textColor,
				polar: true,
				type: 'line',
				height: height || 500,
			},
			pane: {
				size: '80%'
			},
			credits: {
				enabled: false,
			},
			title: {
				text: title,
				style: basicTextStyle,
				// x: -60
			},
			tooltip: {
				pointFormat: '{series.name} <b>{point.percentage:.1f}</b> <br/> <b>{point.y}</b>',
				backgroundColor: styles[theme].altBgColor,
				style: basicTextStyle,
				valuePrefix: valuePrefix || undefined,
			},
			legend: {
				itemStyle: basicTextStyle,
				enabled: true
			},
			xAxis: {
				categories: categories,
				tickmarkPlacement: 'on',
				lineWidth: 0,
				labels: {
					style: basicTextStyle
				},
			},
			yAxis: {
				gridLineInterpolation: 'polygon',
				lineWidth: 0,
				labels: {
					style: basicTextStyle
				},
			},
			responsive: {
				rules: [{
					condition: {
						maxWidth: 500
					},
					chartOptions: {
						legend: {
							align: 'center',
							verticalAlign: 'bottom',
							layout: 'horizontal'
						},
						pane: {
							size: '70%'
						}
					}
				}]
			},
			series: updateSeries(series, data),
		});
	
	useEffect(() => {
		setChartOptions(() => {
			return {
				...chartOptions,
				colors: colors || genericColors,
				chart: {
					...chartOptions.chart,
					backgroundColor: 'transparent' || styles[theme].cardBgColor,
					color: styles[theme].textColor,
					height: height || 500,
				},
				tooltip: {
					...chartOptions.tooltip,
					backgroundColor: styles[theme].altBgColor,
					style: basicTextStyle,
					valuePrefix: valuePrefix || undefined,
				},
				xAxis: {
					...chartOptions.xAxis,
					categories: categories,
				},
				title: {
					...chartOptions.title,
					text: title,
					style: basicTextStyle
				},
				legend: {
					...chartOptions.legend,
					itemStyle: basicTextStyle,
				},
				series: updateSeries(series, data)
			}
		})
	}, [theme, isTabletScreen, series, title, valuePrefix, categories, colors, height]);
	
	return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
}

RadarChart.displayName = "RadarChart"

export default withErrorBoundary(RadarChart, GeneralErrorBoundary);
